import { motion } from 'framer-motion';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Header } from '@/components/Header';
import { TrackTile } from '@/components/TrackTile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useArtistTracks } from '@/hooks/useMusicTracks';
import { useUserPlaylists } from '@/hooks/usePlaylists';
import { usePlayer } from '@/contexts/PlayerContext';
import { genUserName } from '@/lib/genUserName';
import { 
  User, 
  Music2, 
  ListMusic, 
  TrendingUp,
  Edit,
  Share2,
  Zap,
  MapPin,
} from 'lucide-react';
import type { NostrMetadata } from '@nostrify/nostrify';

export function MyProfilePage() {
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey);
  const metadata: NostrMetadata | undefined = author.data?.metadata;
  const { data: userTracks } = useArtistTracks(user?.pubkey || '');
  const { data: userPlaylists } = useUserPlaylists();
  
  const {
    currentTrack,
    setCurrentTrack,
    setPlaylist,
    isPlaying,
    togglePlay,
  } = usePlayer();

  if (!user) {
    window.location.href = '/';
    return null;
  }

  const displayName = metadata?.name || metadata?.display_name || genUserName(user.pubkey);
  const avatarUrl = metadata?.picture;
  const bannerUrl = metadata?.banner;
  const about = metadata?.about;
  const website = metadata?.website;
  const lnAddress = metadata?.lud16 || metadata?.lud06;
  const nip05 = metadata?.nip05;
  const npub = nip19.npubEncode(user.pubkey);

  const isArtist = (userTracks?.length || 0) > 0;

  useSeoMeta({
    title: `${displayName} - My Profile | Cove`,
    description: about || 'My profile on Cove',
  });

  const handleTrackSelect = (track: typeof userTracks extends (infer T)[] | undefined ? T : never) => {
    if (!userTracks) return;
    setPlaylist(userTracks);
    if (currentTrack?.id === track.id) {
      togglePlay();
    } else {
      setCurrentTrack(track);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Ambient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      <Header />

      <main className="container mx-auto px-6 py-8 relative">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Profile Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="glass-card border-white/10 overflow-hidden">
              {bannerUrl && (
                <div className="relative h-48 overflow-hidden">
                  <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                </div>
              )}

              <CardContent className={bannerUrl ? '-mt-16 relative p-6' : 'p-6'}>
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  <Avatar className="h-24 w-24 ring-4 ring-background shadow-2xl">
                    <AvatarImage src={avatarUrl} />
                    <AvatarFallback className="text-3xl bg-gradient-to-br from-white/10 to-white/5">
                      <User className="h-12 w-12 text-white/30" />
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 space-y-3">
                    <div>
                      <h1 className="text-2xl font-bold">{displayName}</h1>
                      <p className="text-xs text-muted-foreground font-mono">
                        {npub.slice(0, 16)}...{npub.slice(-8)}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {isArtist && (
                        <Badge className="bg-primary/20 text-primary border-primary/30">
                          <Music2 className="h-3 w-3 mr-1" />
                          Artist
                        </Badge>
                      )}
                      {nip05 && (
                        <Badge variant="outline" className="border-white/20">
                          ✓ {nip05}
                        </Badge>
                      )}
                    </div>

                    {about && (
                      <p className="text-sm text-muted-foreground">{about}</p>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Link to="/edit-profile">
                        <Button variant="outline" size="sm" className="glass-subtle border-white/10">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </Link>
                      <Button variant="outline" size="sm" className="glass-subtle border-white/10">
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Content Tabs */}
          <Tabs defaultValue="tracks" className="w-full">
            <TabsList className="glass-subtle border border-white/10 w-full justify-start">
              {isArtist && (
                <TabsTrigger value="tracks" className="data-[state=active]:bg-primary/20">
                  <Music2 className="h-4 w-4 mr-2" />
                  Tracks ({userTracks?.length || 0})
                </TabsTrigger>
              )}
              <TabsTrigger value="playlists" className="data-[state=active]:bg-primary/20">
                <ListMusic className="h-4 w-4 mr-2" />
                Playlists ({userPlaylists?.length || 0})
              </TabsTrigger>
            </TabsList>

            {isArtist && (
              <TabsContent value="tracks" className="mt-6">
                {userTracks && userTracks.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {userTracks.map((track, index) => (
                      <TrackTile
                        key={track.id}
                        track={track}
                        onPlay={() => handleTrackSelect(track)}
                        isPlaying={currentTrack?.id === track.id && isPlaying}
                        isSelected={currentTrack?.id === track.id}
                        index={index}
                        className="w-full"
                      />
                    ))}
                  </div>
                ) : (
                  <Card className="glass-subtle border-dashed border-white/20">
                    <CardContent className="py-12 text-center">
                      <Music2 className="h-12 w-12 mx-auto text-white/20 mb-4" />
                      <p className="text-muted-foreground">No tracks yet</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}

            <TabsContent value="playlists" className="mt-6">
              {userPlaylists && userPlaylists.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {userPlaylists.map((playlist) => (
                    <Card key={playlist.id} className="glass-card border-white/10 hover:border-white/20 transition-all cursor-pointer">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{playlist.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {playlist.description || 'No description'}
                        </p>
                        <Badge variant="outline" className="border-white/20">
                          {playlist.trackCount} tracks
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="glass-subtle border-dashed border-white/20">
                  <CardContent className="py-12 text-center">
                    <ListMusic className="h-12 w-12 mx-auto text-white/20 mb-4" />
                    <p className="text-muted-foreground">No playlists yet</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

    </div>
  );
}
